/**
 * Live subscription to a job's captureState in suite-io's RTDB (nyc-suite).
 *
 * Returns { state, loading, error } where state is one of:
 *   null                                  ← not subscribed yet
 *   { phase: "idle", ... }
 *   { phase: "armed", rosterEntryId, subjectName, leaseId, ... }
 *   { phase: "counting_down", ..., expiresAt }
 *   { phase: "capturing", ..., captureAttemptId }
 *
 * Schema: see functions/src/types/ingest.ts > CaptureStateSchema.
 *
 * Path: captureState/{jobId} — single live state per job, server-writable only.
 */

import { useEffect, useState } from "react";
import { rtdb, dbRef, onValue, off } from "../firebase.js";

const INITIAL_STATE = {
  phase: "idle",
  sessionId: null,
  rosterEntryId: null,
  subjectId: null,
  subjectName: null,
  subjectDisplayId: null,
  subjectType: null,
  ownerDeviceId: null,
  ownerUserId: null,
  leaseId: null,
  version: 0,
  updatedAt: 0,
  expiresAt: null,
  captureAttemptId: null,
};

export const useCaptureState = (jobId) => {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!jobId) {
      setState(null);
      return;
    }

    let cancelled = false;
    const listenerRef = dbRef(rtdb, `captureState/${jobId}`);
    const handler = (snapshot) => {
      if (cancelled) return;
      setState(snapshot.val() ?? INITIAL_STATE);
    };
    onValue(listenerRef, handler, (err) => {
      if (cancelled) return;
      console.error("[captureState] RTDB error", err);
      off(listenerRef, "value", handler);
      setError(err);
    });

    return () => {
      cancelled = true;
      off(listenerRef, "value", handler);
    };
  }, [jobId]);

  return { state, loading: state === null && !error, error };
};
